"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMyGroups } from "@/lib/hooks/useMyGroups";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useSetFavourite } from "@/lib/hooks/useMembers";
import { memberCountLabel } from "@/lib/counts";
import { activeGroupSlug } from "@/lib/groupSlug";

const MAX_GROUPS = 10;

function StarIcon({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

/**
 * THE ORDER IS THE SERVER'S (§1.4).
 *
 * This component used to sort the array it was handed — favourites first, then
 * unread descending, then name — which was only correct because
 * `/theta/my-groups` returned EVERY group in one response. Under cursor paging
 * (D4) re-sorting what happens to have arrived reorders a partial list, so
 * `?sort=favourite` does it in the one place that can see all the rows:
 * favourites first, then by the latest post (OSO-676 — it launched as
 * favourites-then-name).
 *
 * The unread-count tiebreak of the legacy sidebar is still gone, because
 * `/groups?sort=` has no arm for it — but a group with NEW posts now floats
 * up on its own, since new posts move the activity key. The badge still
 * shows.
 */
export function GroupSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { data: groups, isLoading } = useMyGroups();
  const { data: currentUser } = useCurrentUser();
  const pathname = usePathname();
  const setFavourite = useSetFavourite();

  const allGroups = groups ?? [];
  const totalGroups = allGroups.length;
  const visibleGroups = allGroups.slice(0, MAX_GROUPS);

  /**
   * A VALUE, NOT A TOGGLE. `PUT …/favourite` took no body and flipped whatever
   * the row happened to be, so two tabs tapping the star twice left it in
   * whichever state the race decided. `PATCH …/membership {isFavourite}`
   * states the value it wants.
   */
  function handleToggleFavourite(e: React.MouseEvent, slug: string, next: boolean) {
    e.preventDefault();
    e.stopPropagation();
    setFavourite.mutate({ ref: slug, isFavourite: next });
  }

  return (
    /**
     * THE CEILING AGREES WITH THE STICKY OFFSET (OSO-591). Every column that
     * holds this card pins it at `top-[4.5rem]`, so the room below the offset
     * is `100vh - 4.5rem`; the old `-2rem` let the card grow 2.5rem past it
     * and pushed the footer — "Opret gruppe" — under the fold. The extra 1rem
     * is the bottom gutter matching the column's `py-4`.
     */
    <div className="w-72 bg-surface rounded-xl border border-border flex flex-col max-h-[calc(100vh-5.5rem)] shrink-0">
      <div className="p-4 border-b border-border">
        <Link href="/grupper" onClick={onNavigate}>
          <h1 className="text-xl font-bold text-foreground">Grupper</h1>
          <p className="text-xs text-subtle-foreground mt-0.5">Dine fællesskaber</p>
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-xl bg-muted" />
                <div className="flex-1">
                  <div className="h-3 bg-muted rounded w-3/4" />
                  <div className="h-2 bg-muted rounded w-1/2 mt-1.5" />
                </div>
              </div>
            ))}
          </div>
        )}

        {visibleGroups.length > 0 && visibleGroups.map((group) => {
          const href = `/grupper/${group.slug}`;
          const isActive = group.slug === activeGroupSlug(pathname);
          const isFavourite = group.isFavourite;
          return (
            <Link
              key={group.id}
              href={href}
              /**
               * NO VIEWPORT PREFETCH. Every row here is a `<Link>`, and Next
               * prefetches each one that is on screen: on a feed load measured
               * against production this list alone was 22 of the page's 41 RSC
               * prefetches, all of them in flight while `GET /feed` and the
               * cards' own media were still arriving. The list is as long as
               * the viewer's membership count and exactly one row of it gets
               * clicked, so the router paid for a dozen route trees to save
               * one navigation.
               *
               * The nav links below keep theirs: there are four of them, they
               * do not grow with the account, and they are where people
               * actually go next.
               */
              prefetch={false}
              /** `page` only when the row IS the page — see `GroupAdminSidebar`. */
              aria-current={isActive ? (pathname === href ? "page" : true) : undefined}
              onClick={onNavigate}
              className={`group w-full text-left px-4 py-3 flex items-center gap-3 transition-colors block ${
                isActive
                  ? "bg-primary-muted"
                  : "hover:bg-surface-2"
              }`}
            >
              {group.coverImageUrl ? (
                <img
                  src={group.coverImageUrl}
                  alt=""
                  className="w-10 h-10 rounded-xl object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-primary-muted/50 flex items-center justify-center text-primary font-bold text-sm">
                  {group.name[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium truncate ${
                    isActive ? "text-indigo-900 dark:text-indigo-100" : "text-foreground"
                  }`}
                >
                  {group.name}
                </p>
                <p className="text-xs text-subtle-foreground">
                  {memberCountLabel(group.memberCount)}
                </p>
              </div>
              <button
                onClick={(e) => handleToggleFavourite(e, group.slug, !isFavourite)}
                className={`flex-shrink-0 p-1 rounded transition-colors ${
                  isFavourite
                    ? "text-amber-400 hover:text-amber-500"
                    : "pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:focus-visible:opacity-100 text-gray-300 hover:text-amber-400"
                }`}
                title={isFavourite ? "Fjern fra favoritter" : "Tilføj til favoritter"}
              >
                <StarIcon filled={isFavourite} />
              </button>
              {group.unreadCount > 0 && (
                <span className="ml-2 shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-full bg-indigo-600 text-white text-xs font-medium">
                  {group.unreadCount}
                </span>
              )}
            </Link>
          );
        })}

        {totalGroups > MAX_GROUPS && (
          <Link
            href="/grupper/mine"
            onClick={onNavigate}
            className="block px-4 py-2 text-xs text-subtle-foreground hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            Vis alle {totalGroups} grupper
          </Link>
        )}
      </div>

      <Link
        href="/begivenheder"
        onClick={onNavigate}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors block border-t border-border ${
          pathname.startsWith("/begivenheder")
            ? "bg-primary-muted"
            : "hover:bg-surface-2"
        }`}
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          pathname.startsWith("/begivenheder")
            ? "bg-primary-muted/50 text-primary"
            : "bg-muted text-muted-foreground"
        }`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${
            pathname.startsWith("/begivenheder") ? "text-indigo-900 dark:text-indigo-100" : "text-foreground"
          }`}>
            Begivenheder
          </p>
          <p className="text-xs text-subtle-foreground">Kommende events</p>
        </div>
      </Link>

      <div className="p-3 border-t border-border space-y-2">
        {currentUser && (
          <Link
            href="/grupper/opret"
            onClick={onNavigate}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Opret gruppe
          </Link>
        )}
        <Link
          href="/grupper"
          onClick={onNavigate}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-primary bg-primary-muted hover:bg-indigo-100 dark:hover:bg-indigo-900 rounded-lg transition-colors"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          Opdag nye grupper
        </Link>
      </div>
    </div>
  );
}
